// NEGF Transport: types of the API (/negf/*, the negf.* jobs), the spec the workspace builds, and the figures.
import { get, patch, post, type Job } from "./api";
import { defaultField, defaultFieldSweep, describeField, fieldActive, fieldProblems, fieldSpec, type EdgesForm, type FieldForm,
  type FieldSweepForm, type ModifyForm } from "./fields";
import type { ContinuumForm } from "./continuum";
import { defaultSc, describeSc, scProblems, scSpec, type ScCalc, type ScForm } from "./sc";

/** A saved SKFitter fit a model can be built from (GET /negf/fits). */
export interface NegfFit {
  project_id: string;
  project_name: string;
  fit_job: string;
  kind: string;
  seed: string;
  rmse_meV: number | null;
  created_at: string | null;
  current: boolean;
  non_orthogonal: boolean | null;
  info: { total_dim?: number; base_dim?: number; soc?: boolean; magnetic?: boolean; n_neighbors?: number; spacegroup?: string };
  model_params: Record<string, unknown>;
}

/** The model in a few numbers (prayog_negf.jobs.model_summary). */
export interface ModelSummary {
  hash: string;
  n_sites: number;
  species: string[];
  formula: string;
  orbitals: number[];
  orbitals_per_cell: number;
  site_orbitals: string[][];
  spinful: boolean;
  soc: boolean;
  magnetic: boolean;
  non_orthogonal: boolean;
  n_neighbors: number;
  lattice: number[][];
  lengths: number[];
  frac_coords: number[][];
  source: Record<string, unknown>;
  spin_resolved_possible: boolean;
  kind?: "sk" | "wannier";
  reach?: number[];                 // largest hopping range in cells along a1, a2, a3
  n_hoppings?: number | null;
  warnings?: string[];              // e.g. Wannier centres far from every atom
  modifiers?: Record<string, any>[];  // a modified model: its changes (prayog_tb modifiers)
  two_d?: boolean;                  // a 2D material (no hoppings along one lattice direction)
  valleys?: Record<string, number[]>;   // K, K' of a hexagonal 2D lattice (fractional)
}

/** The device drawn from the model (POST /negf/device). */
export interface DeviceGeometry {
  errors: string[];
  min_lead_cells?: number;               // the lead unit the model's longest hopping needs
  orbitals?: number;
  sites_total?: number;
  truncated?: boolean;
  positions?: number[][];
  species?: string[];
  region?: string[];
  cell?: number[][];
  transport_axis?: number;
  transport_vector?: number[];
  cell_sites?: number;
  spin_resolved?: boolean;
  limit_orbitals?: number | null;
  within_limit?: boolean;
  model?: ModelSummary;
  frame_positions?: number[][];          // atoms in the device frame (x along transport, y across, z normal)
  frame_extent?: number[][];             // [min xyz, max xyz] of the scattering region in that frame
  electrostatics?: ElectrostaticsView;
}

export interface Box { x?: number[]; y?: number[]; z?: number[] }
/** Gates, dielectrics and the Poisson grid of the electrostatics settings (POST /negf/device). */
export interface ElectrostaticsView {
  errors: string[];
  gates?: { name: string; voltage: number; box: Box }[];
  dielectrics?: { box: Box; eps_r: number }[];
  x_ends?: number[];
  grid_shape?: number[];
  grid_points?: number;
  grid_bounds?: number[][];
}

export type Calc = "transmission" | "iv" | "ldos" | "scf" | "scf_iv" | "field_sweep" | "lead_bands" | "transfer_curve" | "sc_conductance" | "sc_map" | "sc_abs" | "sc_ldos" | "cb_map";
export const CALCS: { value: Calc; label: string }[] = [
  { value: "transmission", label: "T(E)" },
  { value: "iv", label: "I–V" },
  { value: "ldos", label: "LDOS" },
  { value: "scf", label: "SC potential" },
  { value: "scf_iv", label: "SC I–V" },
  { value: "field_sweep", label: "T(B, E)" },
  { value: "lead_bands", label: "Lead bands" },
  { value: "transfer_curve", label: "Id–Vg" },
  { value: "sc_conductance", label: "G(V) · SC" },
  { value: "sc_map", label: "G(V, X) map · SC" },
  { value: "sc_abs", label: "Andreev levels · SC" },
  { value: "sc_ldos", label: "BdG LDOS · SC" },
  { value: "cb_map", label: "Coulomb diamonds" },
];
export const SC_CALCS: Calc[] = ["sc_conductance", "sc_map", "sc_abs", "sc_ldos", "cb_map"];
export const isSc = (c: Calc) => SC_CALCS.includes(c);
export const SCF_CALCS: Calc[] = ["scf", "scf_iv"];
export const isScf = (c: Calc) => SCF_CALCS.includes(c);

export type GateSide = "below" | "above" | "y-" | "y+";
export const GATE_SIDES: { value: GateSide; label: string }[] = [
  { value: "below", label: "Below (back gate)" }, { value: "above", label: "Above (top gate)" },
  { value: "y-", label: "Side, −y (in plane)" }, { value: "y+", label: "Side, +y (in plane)" },
];
/** A plane gate at a distance from the device (scf.gate_boxes on the server). */
export interface GateForm {
  name: string;
  side: GateSide;
  distance: number;          // Å from the outermost atoms on that side
  thickness: number;         // Å
  voltage: number;           // V
  eps_r: number | null;      // dielectric between gate and device (null: vacuum / background)
  x: number[] | null;        // extent along transport (Å, device frame); null: the whole length
  y: number[] | null;        // extent across (top / back gates); null: the whole width
}
/** The electrostatics of a self-consistent run (scf.ES_DEFAULTS). */
export interface ElectrostaticsForm {
  temperature_K: number;
  mu: number;                // Fermi level of the leads relative to E_F of the fit (eV)
  eps_r: number;             // background relative permittivity
  grid: number;              // Poisson grid spacing (Å)
  vacuum: number;            // margin around the atoms (Å)
  doping: number;            // fixed charge per atom (e; + donors)
  gates: GateForm[];
  tol_meV: number;
  max_iter: number;
  mixing: number;
  dE: number;                // energy step of the bias window (eV)
  poisson?: boolean;         // false: no electrostatics (Hubbard U alone)
  hubbard?: HubbardForm;
}

/** Mean-field Hubbard U (magnetism): U per element, the starting state, magnetic leads. */
export interface HubbardForm {
  enabled: boolean;
  U: Record<string, number>;
  seed: "auto" | "staggered" | "ferro" | "random";
  lead_magnetism: boolean;
}
export const HUBBARD_SEEDS = [
  { value: "auto", label: "Automatic (staggered on a bipartite lattice)" },
  { value: "staggered", label: "Staggered (antiferromagnetic)" },
  { value: "ferro", label: "Ferromagnetic" },
  { value: "random", label: "Random" },
];
export function defaultHubbard(species: string[] = []): HubbardForm {
  return { enabled: false, U: Object.fromEntries([...new Set(species)].map((el) => [el, 2.7])), seed: "auto", lead_magnetism: true };
}

/** Transfer-curve (Id–Vg) sweep parameters: gate voltage range, coupling, bias, temperature and energy grid. */
export interface TransferCurveForm {
  Vg_start: number;       // V
  Vg_stop: number;        // V
  n_Vg: number;           // gate points
  gate_E_per_Vg: [number, number, number];  // E field per gate volt [Ex, Ey, Ez] V/Å/V
  bias_V: number;         // drain–source bias (V)
  temperature_K: number;
  mu_eV: number;          // Fermi level shift from the fit's E_F (eV)
  dE_eV: number;          // energy step for the bias window (eV)
}

export function defaultTransferCurve(): TransferCurveForm {
  return { Vg_start: -1, Vg_stop: 1, n_Vg: 11,
    gate_E_per_Vg: [0, 0, 0.02564], bias_V: 0.01, temperature_K: 300, mu_eV: 0, dE_eV: 0.002 };
}

/** The device part of a spec, as the form holds it. */
export interface DeviceForm {
  transport_axis: number;
  length: number;
  cells: Record<string, number>;        // transverse axis -> repetitions
  lead_cells: number;
  supercell: number[][] | null;         // rows in units of the fit's cell; null = the fit's cell
}

export interface CalcForm {
  name: string;
  scf_iv?: { bias_min: number; bias_max: number; n: number; dE: number };
  energies: { min: number; max: number; n: number };
  spin: "auto" | "off";
  iv: { bias_min: number; bias_max: number; n: number; mu: number; temperature_K: number; mode: "rigid" | "linear"; dE: number };
  ldos: { energy: number };
  field?: FieldForm;                     // B and E of every calculation (off: zero)
  field_sweep?: FieldSweepForm;          // T(B, E): |B| from B_min to B_max along a direction
  lead_bands?: { nk: number };
  transfer_curve?: TransferCurveForm;
}

/** What a project remembers (project.settings.negf). */
export interface NegfMemory {
  source?: { kind: "fit"; fit_job: string; project_name: string; seed: string } | { kind: "upload"; name: string }
    | { kind: "wannier"; name: string; spin: WannierSpin } | { kind: "example"; name: string; example: string }
    | { kind: "continuum"; name: string }
    | { kind: "textbook"; name: string; title: string; params: Record<string, number | boolean> };   // Properties only
  wannier?: WannierForm;
  wannier_run?: string;           // the model is a copy of this Wannier run's model (Tight Binding → Wannier functions)
  model_job?: string;
  model_file?: string;
  summary?: ModelSummary;
  device: DeviceForm;
  calc: Calc;
  form: CalcForm;
  jobs?: Partial<Record<Calc, string>>;  // the run shown for each calculation
  electrostatics?: ElectrostaticsForm;
  sc?: ScForm;
  continuum?: ContinuumForm;             // the Model step's "Continuum wire" tab (phase SC3)                           // superconducting contacts, their field, g-tensor, gate ("Contacts & fields")
  from_example?: { id: string; try: string; title: string; about: string };   // made by an illustrative project's "try this"
  // model tools (phase M2c): the model before its changes (Reset), the changes and the band-edge tools
  base_file?: string;
  base_summary?: ModelSummary;
  modify?: ModifyForm;
  modify_job?: string;                   // a negf.modify job whose model is to be used when it finishes
  edges?: EdgesForm;
  edges_jobs?: Partial<Record<EdgesForm["analysis"], string>>;
}

export function defaultElectrostatics(): ElectrostaticsForm {
  return { temperature_K: 300, mu: 0, eps_r: 1, grid: 1, vacuum: 8, doping: 0, gates: [], tol_meV: 1, max_iter: 60,
    mixing: 0.3, dE: 0.002 };
}

/** Gate presets for teaching: a back gate, a top finger gate over the middle, in-plane split gates. */
export function gatePreset(kind: "back" | "finger" | "split", length: number): GateForm[] {
  const mid = [Math.round(length / 3), Math.round((2 * length) / 3)];
  if (kind === "back") return [{ name: "back gate", side: "below", distance: 10, thickness: 2, voltage: 1, eps_r: 3.9, x: null, y: null }];
  if (kind === "finger") return [{ name: "finger gate", side: "above", distance: 6, thickness: 2, voltage: -0.5, eps_r: 4, x: mid, y: null }];
  return [{ name: "split gate −y", side: "y-", distance: 4, thickness: 2, voltage: -1, eps_r: null, x: mid, y: null },
    { name: "split gate +y", side: "y+", distance: 4, thickness: 2, voltage: -1, eps_r: null, x: mid, y: null }];
}

/** The electrostatics part of a spec: the form without empty optional fields. */
export function esSpec(es: ElectrostaticsForm): Record<string, unknown> {
  const { gates, hubbard, poisson, ...rest } = es;
  const extra: Record<string, unknown> = {};
  if (poisson === false) extra.poisson = false;
  if (hubbard?.enabled) extra.hubbard = { U: hubbard.U, seed: hubbard.seed, lead_magnetism: hubbard.lead_magnetism };
  return {
    ...rest,
    ...extra,
    gates: gates.map((g) => {
      const out: Record<string, unknown> = { name: g.name, side: g.side, distance: g.distance, thickness: g.thickness, voltage: g.voltage };
      if (g.eps_r && g.eps_r > 0) out.eps_r = g.eps_r;
      if (g.x) out.x = g.x;
      if (g.y && (g.side === "below" || g.side === "above")) out.y = g.y;
      return out;
    }),
  };
}

/** Problems of the electrostatics form found before asking the server. */
export function esProblems(es: ElectrostaticsForm): string[] {
  const out: string[] = [];
  if (!(es.temperature_K >= 1)) out.push("Temperature at least 1 K.");
  if (!(es.grid >= 0.2 && es.grid <= 5)) out.push("Grid spacing between 0.2 and 5 Å.");
  if (!(es.eps_r >= 1)) out.push("Background εr at least 1.");
  if (es.hubbard?.enabled) {
    if (Object.values(es.hubbard.U).some((u) => !(u >= 0))) out.push("Hubbard U must be 0 or more.");
    if (!Object.values(es.hubbard.U).some((u) => u > 0)) out.push("Give a Hubbard U above 0 for at least one element.");
  }
  es.gates.forEach((g, i) => {
    if (!(g.distance > 0)) out.push(`Gate ${i + 1}: the distance must be positive.`);
    if (g.x && !(g.x[1] > g.x[0])) out.push(`Gate ${i + 1}: its x range needs to > from.`);
    if (g.eps_r != null && g.eps_r !== 0 && !(g.eps_r >= 1)) out.push(`Gate ${i + 1}: εr at least 1.`);
  });
  return out;
}

export const IDENTITY = [[1, 0, 0], [0, 1, 0], [0, 0, 1]];

export function defaultDevice(): DeviceForm {
  return { transport_axis: 0, length: 6, cells: { "1": 4, "2": 1 }, lead_cells: 1, supercell: null };
}

export function defaultCalcForm(): CalcForm {
  return {
    name: "",
    energies: { min: -2, max: 2, n: 201 },
    spin: "auto",
    iv: { bias_min: -0.5, bias_max: 0.5, n: 11, mu: 0, temperature_K: 300, mode: "rigid", dE: 0.002 },
    ldos: { energy: 0 },
  };
}

export function defaultMemory(): NegfMemory {
  return { device: defaultDevice(), calc: "transmission", form: defaultCalcForm() };
}

/** Transverse axes of a transport axis. */
export function transverse(axis: number): number[] {
  return [0, 1, 2].filter((a) => a !== axis);
}

/** A device whose transport axis changed: the old transport axis becomes transverse (1 cell), the new one leaves. */
export function withAxis(d: DeviceForm, axis: number): DeviceForm {
  const cells: Record<string, number> = {};
  for (const a of transverse(axis)) cells[String(a)] = d.cells[String(a)] ?? 1;
  return { ...d, transport_axis: axis, cells };
}

/** The device part of a job spec (the server fills the defaults it does not get). */
export function deviceSpec(d: DeviceForm): Record<string, unknown> {
  const cells: Record<string, number> = {};
  for (const a of transverse(d.transport_axis)) cells[String(a)] = Math.max(1, Math.round(d.cells[String(a)] ?? 1));
  const spec: Record<string, unknown> = { transport_axis: d.transport_axis, length: d.length, cells, lead_cells: d.lead_cells };
  if (d.supercell && JSON.stringify(d.supercell) !== JSON.stringify(IDENTITY)) spec.supercell = d.supercell;
  return spec;
}

/** The whole spec of a calculation job (without the model, which goes as model_file). */
export function calcSpec(d: DeviceForm, calc: Calc, f: CalcForm, es?: ElectrostaticsForm, sc?: ScForm): Record<string, unknown> {
  const spec: Record<string, unknown> = { ...deviceSpec(d), calculation: calc, name: f.name.trim() };
  if (isSc(calc)) {
    spec.superconductivity = scSpec(sc ?? defaultSc(), calc as ScCalc);          // the field is the step's B with its g-tensor
    return spec;
  }
  if (calc === "transmission") Object.assign(spec, { energies: f.energies, spin: f.spin });
  if (calc === "iv") spec.iv = f.iv;
  if (calc === "ldos") spec.ldos = f.ldos;
  if (calc === "scf") spec.energies = f.energies;
  if (calc === "scf_iv") spec.iv = f.scf_iv ?? defaultScfIv();
  if (isScf(calc)) spec.electrostatics = esSpec(es ?? defaultElectrostatics());
  if (calc === "field_sweep") {
    Object.assign(spec, { energies: f.energies, spin: f.spin, field_sweep: f.field_sweep ?? defaultFieldSweep() });
    spec.field = fieldSpec({ ...(f.field ?? defaultField()), B: [0, 0, 0] });         // |B| comes from the sweep
  } else if (fieldActive(f.field)) spec.field = fieldSpec(f.field!);
  if (calc === "lead_bands") Object.assign(spec, { energies: f.energies, lead_bands: f.lead_bands ?? { nk: 201 } });
  if (calc === "transfer_curve") {
    spec.transfer_curve = f.transfer_curve ?? defaultTransferCurve();
    if (fieldActive(f.field)) spec.field = fieldSpec(f.field!);
  }
  return spec;
}

export function defaultScfIv() {
  return { bias_min: -0.3, bias_max: 0.3, n: 7, dE: 0.002 };
}

/** Determinant of a 3x3 integer matrix (the supercell must not be singular; |det| cells of the fit per new cell). */
export function det3(m: number[][]): number {
  return m[0][0] * (m[1][1] * m[2][2] - m[1][2] * m[2][1]) - m[0][1] * (m[1][0] * m[2][2] - m[1][2] * m[2][0])
    + m[0][2] * (m[1][0] * m[2][1] - m[1][1] * m[2][0]);
}

/** Problems of the calculation form found before asking the server ("" = fine). */
export function calcProblems(calc: Calc, f: CalcForm, transportAxis?: number, lattice?: number[][], sc?: ScForm, length = 6): string[] {
  if (isSc(calc)) return scProblems(sc ?? defaultSc(), length, calc);
  const out: string[] = fieldProblems(f.field, calc === "field_sweep" ? f.field_sweep ?? defaultFieldSweep() : undefined, transportAxis, lattice);
  if (calc === "field_sweep" || calc === "lead_bands") {
    if (!(f.energies.max > f.energies.min)) out.push("The energy range needs max > min.");
  }
  if (calc === "field_sweep" && !(f.energies.n >= 2)) out.push("At least 2 energy points.");
  if (calc === "lead_bands" && !((f.lead_bands?.nk ?? 201) >= 2)) out.push("At least 2 k-points.");
  if (calc === "transmission") {
    if (!(f.energies.max > f.energies.min)) out.push("The energy range needs max > min.");
    if (!(f.energies.n >= 2)) out.push("At least 2 energy points.");
  }
  if (calc === "scf") {
    if (!(f.energies.max > f.energies.min)) out.push("The energy range needs max > min.");
    if (!(f.energies.n >= 2)) out.push("At least 2 energy points.");
  }
  if (calc === "scf_iv") {
    const v = f.scf_iv ?? defaultScfIv();
    if (!(v.bias_max > v.bias_min)) out.push("The bias range needs max > min.");
    if (!(v.n >= 2)) out.push("At least 2 bias points.");
    if (!(v.dE > 0)) out.push("The energy step dE must be positive.");
  }
  if (calc === "iv") {
    if (!(f.iv.bias_max > f.iv.bias_min)) out.push("The bias range needs max > min.");
    if (!(f.iv.n >= 2)) out.push("At least 2 bias points.");
    if (!(f.iv.dE > 0)) out.push("The energy step dE must be positive.");
    if (!(f.iv.temperature_K >= 0)) out.push("The temperature cannot be negative.");
  }
  if (calc === "transfer_curve") {
    const tc = f.transfer_curve ?? defaultTransferCurve();
    if (!(tc.Vg_stop > tc.Vg_start)) out.push("Transfer curve: Vg stop must be greater than Vg start.");
    if (!(tc.n_Vg >= 2)) out.push("Transfer curve: at least 2 gate points.");
    if (!(tc.temperature_K >= 1)) out.push("Transfer curve: temperature at least 1 K.");
    if (!(tc.dE_eV > 0)) out.push("Transfer curve: energy step dE must be positive.");
  }
  return out;
}

/** "Transmission · zigzag 8 · -2…2 eV, 201 points" for the list of runs. */
export function describeRun(params: Record<string, any>): string {
  const s = (params.spec ?? {}) as Record<string, any>;
  if (SC_CALCS.includes(s.calculation)) return describeSc(s);
  const name = s.name ? `${s.name} · ` : "";
  const fd = describeField(s.field);
  const dev = `L=${s.length ?? "?"}${fd ? `, ${fd}` : ""}`;
  if (s.calculation === "field_sweep") return `${name}T(B, E): |B| ${s.field_sweep?.B_min}…${s.field_sweep?.B_max} T (${s.field_sweep?.n} fields) ${s.field_sweep?.direction ?? ""}, ${s.energies?.min}…${s.energies?.max} eV, L=${s.length ?? "?"}`;
  if (s.calculation === "lead_bands") return `${name}lead bands (${s.lead_bands?.nk} k-points), ${dev}`;
  if (s.calculation === "iv") return `${name}I–V ${s.iv?.mode ?? "rigid"}, ${s.iv?.bias_min}…${s.iv?.bias_max} V (${s.iv?.n} points), ${dev}`;
  if (s.calculation === "ldos") return `${name}LDOS at ${s.ldos?.energy ?? 0} eV, ${dev}`;
  const gates = (s.electrostatics?.gates ?? []).map((g: any) => `${g.voltage} V`).join(", ");
  const hub = s.electrostatics?.hubbard?.U;
  const hubText = hub ? `, U ${typeof hub === "object" ? Object.entries(hub).map(([k, v]) => `${k} ${v}`).join(" ") : hub} eV` : "";
  const es = `μ ${s.electrostatics?.mu ?? 0} eV${gates ? `, gates ${gates}` : ""}${hubText}`;
  if (s.calculation === "scf") return `${name}self-consistent, ${es}, ${dev}`;
  if (s.calculation === "scf_iv") return `${name}self-consistent I–V ${s.iv?.bias_min}…${s.iv?.bias_max} V (${s.iv?.n} points), ${es}, ${dev}`;
  if (s.calculation === "transfer_curve") { const tc = s.transfer_curve ?? {}; return `${name}Id–Vg ${tc.Vg_start}…${tc.Vg_stop} V (${tc.n_Vg} points), Vds=${tc.bias_V} V, ${dev}`; }
  return `${name}T(E) ${s.energies?.min}…${s.energies?.max} eV (${s.energies?.n} points), ${dev}`;
}

export const REGION_COLORS: Record<string, string> = { "left lead": "#eb6834", device: "#2a78d6", "right lead": "#1baf7a" };

/** The device view: atoms by region, 2D when the device is flat (graphene, a ribbon), else 3D. */
export function deviceFigure(g: DeviceGeometry): { data: unknown[]; layout: Record<string, unknown> } {
  const pos = g.positions ?? [];
  const spread = [0, 1, 2].map((a) => (pos.length ? Math.max(...pos.map((p) => p[a])) - Math.min(...pos.map((p) => p[a])) : 0));
  const flat = spread.filter((s) => s > 1e-3).length <= 2;
  const [x, y] = [0, 1, 2].sort((a, b) => spread[b] - spread[a]).slice(0, 2).sort();
  const regions = ["left lead", "device", "right lead"];
  const data = regions.map((r) => {
    const idx = pos.map((_, i) => i).filter((i) => g.region?.[i] === r);
    const text = idx.map((i) => `${g.species?.[i] ?? ""} (${pos[i].map((v) => v.toFixed(2)).join(", ")}) Å`);
    const common = { name: r, mode: "markers", text, hoverinfo: "text", marker: { color: REGION_COLORS[r], size: flat ? 7 : 4 } };
    return flat
      ? { ...common, type: "scatter", x: idx.map((i) => pos[i][x]), y: idx.map((i) => pos[i][y]) }
      : { ...common, type: "scatter3d", x: idx.map((i) => pos[i][0]), y: idx.map((i) => pos[i][1]), z: idx.map((i) => pos[i][2]) };
  });
  const ax = "xyz";
  const layout: Record<string, unknown> = flat
    ? { title: { text: "Device: leads and scattering region" }, xaxis: { title: { text: `${ax[x]} (Å)` } },
        yaxis: { title: { text: `${ax[y]} (Å)` }, scaleanchor: "x", scaleratio: 1 }, legend: { orientation: "h", y: -0.2 } }
    : { title: { text: "Device: leads and scattering region" }, legend: { orientation: "h" },
        scene: { aspectmode: "data", xaxis: { title: { text: "x (Å)" } }, yaxis: { title: { text: "y (Å)" } }, zaxis: { title: { text: "z (Å)" } } } };
  return { data, layout };
}

/** T(E) with the open channels (dotted) and, for collinear magnets, T_up and T_down. */
export function transmissionFigure(r: Record<string, any>) {
  const data: unknown[] = [
    { type: "scatter", mode: "lines", name: "T(E)", x: r.E, y: r.T, line: { color: "#2e213e", width: 2 } },
    { type: "scatter", mode: "lines", name: "open channels", x: r.E, y: r.channels, line: { color: "#9a9aa0", width: 1, dash: "dot", shape: "hv" } },
  ];
  if (r.T_up) {
    data.push({ type: "scatter", mode: "lines", name: "spin up", x: r.E, y: r.T_up, line: { color: "#c0392b", width: 1.3 } });
    data.push({ type: "scatter", mode: "lines", name: "spin down", x: r.E, y: r.T_down, line: { color: "#2471a3", width: 1.3 } });
  }
  return { data, layout: { title: { text: r.name || "Transmission" }, xaxis: { title: { text: "E − E<sub>F</sub> (eV)" } },
    yaxis: { title: { text: "Transmission T(E)" }, rangemode: "tozero" }, legend: { orientation: "h", y: -0.2 } } };
}

export const G0_SIEMENS = 7.748091729e-5;

/** I(V) in µA and dI/dV in units of G0 = 2e²/h on a second axis. */
export function ivFigure(r: Record<string, any>) {
  const I = (r.I as number[]).map((v) => v * 1e6);
  const G = (r.G as number[]).map((v) => v / G0_SIEMENS);
  return {
    data: [
      { type: "scatter", mode: "lines+markers", name: "I", x: r.V, y: I, line: { color: "#2e213e" }, marker: { size: 5 } },
      { type: "scatter", mode: "lines", name: "dI/dV", x: r.V, y: G, yaxis: "y2", line: { color: "#eb6834", dash: "dash" } },
    ],
    layout: { title: { text: `${r.name || "I–V"} (${r.mode} drop, not self-consistent)` }, xaxis: { title: { text: "Bias V (V)" } },
      yaxis: { title: { text: "Current I (µA)" } },
      yaxis2: { title: { text: "dI/dV (2e²/h)" }, overlaying: "y", side: "right", rangemode: "tozero", showgrid: false },
      margin: { l: 60, r: 60, t: 40, b: 50 }, legend: { orientation: "h", y: -0.2 } },
  };
}

/** log₁₀(Id) vs Vg for the FET transfer characteristic; the SW value is annotated. */
export function transferCurveFigure(r: Record<string, any>) {
  const Vg = r.Vg_V as number[];
  const log10I = (r.log10_I as (number | null)[]).map((v) => (v === null ? null : v));
  const sw = r.subthreshold_swing_V_per_dec;
  const swText = sw === null || !isFinite(sw) ? "" : ` · SW = ${(sw * 1000).toFixed(1)} mV/dec`;
  return {
    data: [
      { type: "scatter", mode: "lines+markers", name: "log₁₀|Id|", x: Vg, y: log10I,
        line: { color: "#2e213e", width: 2 }, marker: { size: 5 }, connectgaps: false },
    ],
    layout: {
      title: { text: `${r.name || "Transfer curve"}: Id–Vg${swText}` },
      xaxis: { title: { text: "Gate voltage Vg (V)" } },
      yaxis: { title: { text: "log₁₀|Id| (log₁₀ A)" } },
      legend: { orientation: "h", y: -0.2 },
    },
  };
}

/** LDOS per site, coloured, on the two widest axes of the device. */
export function ldosFigure(r: Record<string, any>) {
  const pos = r.positions as number[][];
  const spread = [0, 1, 2].map((a) => Math.max(...pos.map((p) => p[a])) - Math.min(...pos.map((p) => p[a])));
  const [x, y] = [0, 1, 2].sort((a, b) => spread[b] - spread[a]).slice(0, 2).sort();
  return {
    data: [{ type: "scatter", mode: "markers", x: pos.map((p) => p[x]), y: pos.map((p) => p[y]), text: (r.ldos as number[]).map((v) => v.toPrecision(4)),
      hovertemplate: "%{x:.2f}, %{y:.2f} Å<br>LDOS %{text} /eV<extra></extra>",
      marker: { color: r.ldos, colorscale: "Magma", size: 9, colorbar: { title: { text: "states/eV" } } } }],
    layout: { title: { text: `${r.name || "LDOS"} at E − E<sub>F</sub> = ${r.energy} eV` }, xaxis: { title: { text: `${"xyz"[x]} (Å)` } },
      yaxis: { title: { text: `${"xyz"[y]} (Å)` }, scaleanchor: "x", scaleratio: 1 } },
  };
}

// -- self-consistent results and the electrostatics view ----------------------------------------------------------
const wideAxis = (pos: number[][]) => {
  const spread = [1, 2].map((a) => Math.max(...pos.map((p) => p[a])) - Math.min(...pos.map((p) => p[a])));
  return spread[0] >= spread[1] ? 1 : 2;
};

/** A per-atom quantity on the device frame (x along transport, the wider transverse axis up), diverging colours. */
export function siteMapFigure(pos: number[][], values: number[], title: string, unit: string, scale = "RdBu") {
  const a = wideAxis(pos);
  const vmax = Math.max(1e-12, ...values.map(Math.abs));
  return {
    data: [{ type: "scatter", mode: "markers", x: pos.map((p) => p[0]), y: pos.map((p) => p[a]),
      text: values.map((v) => v.toPrecision(4)), hovertemplate: `x %{x:.2f} Å, ${"xyz"[a]} %{y:.2f} Å<br>%{text} ${unit}<extra></extra>`,
      marker: { color: values, colorscale: scale, cmin: -vmax, cmax: vmax, reversescale: scale === "RdBu", size: 9,
        colorbar: { title: { text: unit } } } }],
    layout: { title: { text: title }, xaxis: { title: { text: "x along transport (Å)" } },
      yaxis: { title: { text: `${"xyz"[a]} (Å)` }, scaleanchor: "x", scaleratio: 1 } },
  };
}

export function scfTransmissionFigure(r: Record<string, any>) {
  const data: unknown[] = [
    { type: "scatter", mode: "lines", name: "flat band", x: r.E, y: r.T_flat, line: { color: "#9a9aa0", width: 1.2 } },
    { type: "scatter", mode: "lines", name: r.T_up ? "self-consistent (spin average)" : "self-consistent", x: r.E, y: r.T,
      line: { color: "#2e213e", width: 2 } },
  ];
  if (r.T_up) {
    data.push({ type: "scatter", mode: "lines", name: "spin up", x: r.E, y: r.T_up, line: { color: "#c0392b", width: 1.2, dash: "dash" } });
    data.push({ type: "scatter", mode: "lines", name: "spin down", x: r.E, y: r.T_down, line: { color: "#2471a3", width: 1.2, dash: "dot" } });
  }
  return {
    data,
    layout: { title: { text: `${r.name || "Self-consistent"}: T(E)` }, xaxis: { title: { text: "E − E<sub>F</sub> (eV)" } },
      yaxis: { title: { text: "Transmission T(E)" }, rangemode: "tozero" }, legend: { orientation: "h", y: -0.2 } },
  };
}

export function profileFigure(r: Record<string, any>) {
  return {
    data: [
      { type: "scatter", mode: "lines+markers", name: "U", x: r.profile_x, y: r.profile_U, line: { color: "#2a78d6" } },
      { type: "scatter", mode: "lines", name: "extra electrons / atom", x: r.profile_x, y: r.profile_dn, yaxis: "y2",
        line: { color: "#eb6834", dash: "dash" } },
    ],
    layout: { title: { text: "Averaged over the cross-section" }, xaxis: { title: { text: "x along transport (Å)" } },
      yaxis: { title: { text: "U (eV)" } }, yaxis2: { title: { text: "extra electrons per atom" }, overlaying: "y", side: "right",
        showgrid: false }, margin: { l: 60, r: 70, t: 40, b: 50 }, legend: { orientation: "h", y: -0.2 } },
  };
}

export function profilesFigure(r: Record<string, any>) {
  const V = r.V as number[];
  return {
    data: (r.profiles_U as number[][]).map((p, i) => ({ type: "scatter", mode: "lines", name: `${V[i] >= 0 ? "+" : ""}${V[i].toFixed(2)} V`,
      x: r.profile_x, y: p, line: { width: 1.5 } })),
    layout: { title: { text: "Self-consistent potential at each bias" }, xaxis: { title: { text: "x along transport (Å)" } },
      yaxis: { title: { text: "U averaged over the cross-section (eV)" } } },
  };
}

export function convergenceFigure(history: { iteration: number; max_dU_meV: number }[]) {
  return {
    data: [{ type: "scatter", mode: "lines+markers", x: history.map((h) => h.iteration), y: history.map((h) => Math.max(h.max_dU_meV, 1e-6)),
      line: { color: "#2e213e" } }],
    layout: { title: { text: "Convergence" }, xaxis: { title: { text: "iteration" }, dtick: 1 },
      yaxis: { title: { text: "max |ΔU| (meV)" }, type: "log" } },
  };
}

/** The device with its gates (gold) and dielectrics (blue) seen from the side (x–z) or from the top (x–y). */
export function electrostaticsFigure(g: DeviceGeometry, view: "side" | "top") {
  const a = view === "side" ? 2 : 1;
  const es = g.electrostatics;
  const pos = g.frame_positions ?? [];
  const region = g.region ?? [];
  const bounds = es?.grid_bounds;
  const lim = (rng: number[] | undefined, axis: number) => [rng?.[0] ?? bounds?.[axis]?.[0] ?? 0, rng?.[1] ?? bounds?.[axis]?.[1] ?? 0];
  const shapes: unknown[] = [];
  const notes: unknown[] = [];
  for (const d of es?.dielectrics ?? []) {
    const [x0, x1] = lim(d.box.x, 0), [y0, y1] = lim(a === 2 ? d.box.z : d.box.y, a);
    shapes.push({ type: "rect", x0, x1, y0, y1, fillcolor: "rgba(42,120,214,0.12)", line: { width: 0 }, layer: "below" });
  }
  for (const gt of es?.gates ?? []) {
    const [x0, x1] = lim(gt.box.x, 0), [y0, y1] = lim(a === 2 ? gt.box.z : gt.box.y, a);
    shapes.push({ type: "rect", x0, x1, y0, y1, fillcolor: "rgba(237,161,0,0.55)", line: { color: "#b37800", width: 1 } });
    notes.push({ x: (x0 + x1) / 2, y: (y0 + y1) / 2, text: `${gt.name}: ${gt.voltage} V`, showarrow: false, font: { size: 10 } });
  }
  for (const x of es?.x_ends ?? []) shapes.push({ type: "line", x0: x, x1: x, y0: bounds?.[a]?.[0] ?? 0, y1: bounds?.[a]?.[1] ?? 1,
    line: { color: "#888", dash: "dot", width: 1 } });
  const colors: Record<string, string> = REGION_COLORS;
  const data = ["left lead", "device", "right lead"].map((r) => {
    const idx = pos.map((_, i) => i).filter((i) => region[i] === r);
    return { type: "scatter", mode: "markers", name: r, x: idx.map((i) => pos[i][0]), y: idx.map((i) => pos[i][a]),
      marker: { color: colors[r], size: 6 }, hoverinfo: "skip" };
  });
  return { data, layout: { title: { text: view === "side" ? "Side view (x–z)" : "Top view (x–y)" }, shapes, annotations: notes,
    xaxis: { title: { text: "x along transport (Å)" }, range: bounds ? [bounds[0][0] - 2, bounds[0][1] + 2] : undefined },
    yaxis: { title: { text: `${"xyz"[a]} (Å)` }, scaleanchor: "x", scaleratio: 1 }, showlegend: false } };
}

// -- Wannier90 models --------------------------------------------------------------------------------------------
export type WannierSpin = "none" | "collinear" | "spinor";
export const WANNIER_SPINS: { value: WannierSpin; label: string }[] = [
  { value: "none", label: "No spin" }, { value: "collinear", label: "Collinear (up + down)" }, { value: "spinor", label: "Spinors (SOC)" },
];
export type WannierRole = "hr" | "hr_dn" | "wout" | "win" | "centres" | "wsvec" | "wsvec_dn";
export interface WannierForm {
  spin: WannierSpin;
  geometry: "wout" | "win";                 // lattice, atoms and centres from the .wout, or .win + _centres.xyz
  files: Partial<Record<WannierRole, string>>;
  spinor_order: "auto" | "interleaved" | "blocks";
  efermi: number | null;                    // eV, the DFT Fermi energy (required)
  hop_cutoff: number;                       // eV
  max_range: number;                        // Å, 0 = no limit
  seed: string;
}
export function defaultWannier(): WannierForm {
  return { spin: "none", geometry: "wout", files: {}, spinor_order: "auto", efermi: null, hop_cutoff: 0.001, max_range: 0, seed: "" };
}

/** The files a Wannier90 model needs (required first), with what each is. */
export function wannierRoles(w: WannierForm): { role: WannierRole; label: string; required: boolean }[] {
  const out: { role: WannierRole; label: string; required: boolean }[] = [];
  if (w.spin === "collinear") {
    out.push({ role: "hr", label: "seedname_up_hr.dat (spin up)", required: true }, { role: "hr_dn", label: "seedname_dn_hr.dat (spin down)", required: true });
  } else {
    out.push({ role: "hr", label: "seedname_hr.dat", required: true });
  }
  if (w.geometry === "wout") out.push({ role: "wout", label: "seedname.wout (lattice, atoms, centres)", required: true });
  else out.push({ role: "win", label: "seedname.win (lattice, atoms)", required: true }, { role: "centres", label: "seedname_centres.xyz", required: true });
  out.push({ role: "wsvec", label: w.spin === "collinear" ? "seedname_up_wsvec.dat (optional)" : "seedname_wsvec.dat (optional)", required: false });
  if (w.spin === "collinear") out.push({ role: "wsvec_dn", label: "seedname_dn_wsvec.dat (optional)", required: false });
  return out;
}

const isDown = (n: string) => /(^|[._-])(dn|down)([._-]|$)/i.test(n);

/** Guess which upload is which Wannier90 file (newest first), keeping choices already made. */
export function guessWannierFiles(w: WannierForm, files: { id: string; name: string; created_at: string }[]): WannierForm["files"] {
  const sorted = [...files].sort((a, b) => b.created_at.localeCompare(a.created_at));
  const pick = (test: (n: string) => boolean) => sorted.find((f) => test(f.name.toLowerCase()))?.id;
  const g: WannierForm["files"] = {
    hr: pick((n) => n.endsWith("_hr.dat") && !(w.spin === "collinear" && isDown(n))),
    hr_dn: w.spin === "collinear" ? pick((n) => n.endsWith("_hr.dat") && isDown(n)) : undefined,
    wout: pick((n) => n.endsWith(".wout") && !(w.spin === "collinear" && isDown(n))),
    win: pick((n) => n.endsWith(".win") && !(w.spin === "collinear" && isDown(n))),
    centres: pick((n) => n.endsWith("_centres.xyz") && !(w.spin === "collinear" && isDown(n))),
    wsvec: pick((n) => n.endsWith("_wsvec.dat") && !(w.spin === "collinear" && isDown(n))),
    wsvec_dn: w.spin === "collinear" ? pick((n) => n.endsWith("_wsvec.dat") && isDown(n)) : undefined,
  };
  const keep = Object.fromEntries(Object.entries(w.files).filter(([, v]) => v && files.some((f) => f.id === v)));
  return Object.fromEntries(Object.entries({ ...g, ...keep }).filter(([, v]) => !!v)) as WannierForm["files"];
}

/** Problems before the model can be built ("" = ready). */
export function wannierProblems(w: WannierForm): string[] {
  const out = wannierRoles(w).filter((r) => r.required && !w.files[r.role]).map((r) => `Choose ${r.label}.`);
  if (w.efermi == null || Number.isNaN(w.efermi)) out.push("Give the Fermi energy of the DFT run (eV).");
  if (!(w.hop_cutoff >= 0)) out.push("The hopping cutoff cannot be negative.");
  return out;
}

/** params.wannier of the negf.model job. */
export function wannierParams(w: WannierForm): Record<string, unknown> {
  const roles = new Set(wannierRoles(w).map((r) => r.role));
  const files = Object.fromEntries(Object.entries(w.files).filter(([k, v]) => v && roles.has(k as WannierRole)));
  return { ...files, spin: w.spin, spinor_order: w.spinor_order, efermi: w.efermi, hop_cutoff: w.hop_cutoff,
    max_range: w.max_range > 0 ? w.max_range : null, seed: w.seed.trim() || "wannier" };
}

// -- API -----------------------------------------------------------------------------------------------------------
export const negfFits = () => get<NegfFit[]>("/negf/fits");
/** "Use in NEGF Transport" (SKFitter's result step): a new NEGF Transport project whose SK model is being built from
 * this fit (the negf.model job is started and remembered, so the project opens on its Model step with the job running). */
export async function fitToNegf(fitJobId: string, projectName: string): Promise<{ id: string }> {
  const f = (await negfFits()).find((x) => x.fit_job === fitJobId);
  if (!f) throw new Error("This fit is not among your saved fits that NEGF Transport can use.");
  const name = `${projectName} - transport`.slice(0, 120);
  const p = await post<{ id: string }>("/projects", { product: "negf", name });
  const j = await post<Job>("/jobs", { kind: "negf.model", project_id: p.id, params: f.model_params });
  const negf: NegfMemory = { ...defaultMemory(), model_job: j.id,
    source: { kind: "fit", fit_job: f.fit_job, project_name: f.project_name, seed: f.seed } };
  await patch(`/projects/${p.id}`, { settings: { negf } });
  return p;
}
export const negfStatus = () => get<{ ok: boolean; kwant?: string; solver?: string; error?: string }>("/negf/status");
export const negfDevice = (model_file: string, spec: Record<string, unknown>, project_id: string) =>
  post<DeviceGeometry>("/negf/device", { model_file, spec, project_id });
